// RLoader — organic R-mark loader for Resolute.
// Ported from the Resolute Design System handoff (loader.jsx). The R-mark is a
// blue "raindrop" + navy "leg" inside a 0 0 100 100 viewBox. Three variants:
//   - liquid:  the mark fills with rippling liquid
//   - breathe: the mark scales + fades in a slow pulse
//   - trace:   the outline draws on, then the fill fades in behind it
import { useRef, useEffect } from "react";

const RAINDROP = "M 11.10 0 L 62.16 0 A 30.2 30.2 0 0 1 62.10 60.40 Z";
const LEG = "M 5.87 40.10 L 44.37 40.10 L 94.17 100 L 54.89 100 Z";

// Wavy liquid surface across the mark's bounding box.
// level: 0 (empty) .. 100 (full), measured from the bottom. amp drives the ripple.
function liquidPath(level, t, amp) {
  const surfaceY = 100 - level;
  const k1 = 0.13;
  const k2 = 0.21;
  const a2 = amp * 0.55;
  const points = [];
  for (let x = -12; x <= 112; x += 4) {
    const y =
      surfaceY +
      amp * Math.sin(k1 * x + t * 1.6) +
      a2 * Math.sin(k2 * x - t * 2.3);
    points.push(`${x.toFixed(1)},${y.toFixed(2)}`);
  }
  return `M ${points.join(" L ")} L 112,112 L -12,112 Z`;
}

function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

export default function RLoader({
  variant = "trace",
  accent = "#338BF7",
  speed = 0.3,
  bgDark = true,
  size = 100,
}) {
  const rafId = useRef(0);
  const startTs = useRef(null);

  const waterRef = useRef(null);
  const shineRef = useRef(null);
  const groupRef = useRef(null);

  const reducedMotion = prefersReducedMotion();

  useEffect(() => {
    if (reducedMotion) return undefined;

    startTs.current = null;
    const loop = (ts) => {
      if (startTs.current == null) startTs.current = ts;
      const elapsed = ((ts - startTs.current) / 1000) * speed;

      if (variant === "liquid") {
        // tide bobs smoothly between 12% and 92% — seamless, no hard reset
        const tide = 52 + 40 * Math.sin(elapsed * 0.9 - Math.PI / 2);
        const amp = 3.2 + 0.8 * Math.sin(elapsed * 1.7);
        if (waterRef.current) waterRef.current.setAttribute("d", liquidPath(tide, elapsed, amp));
        if (shineRef.current) shineRef.current.setAttribute("d", liquidPath(tide - 1.4, elapsed + 0.6, amp * 0.7));
      } else if (variant === "breathe") {
        const s = 0.94 + 0.06 * (0.5 + 0.5 * Math.sin(elapsed * 1.8));
        const o = 0.6 + 0.4 * (0.5 + 0.5 * Math.sin(elapsed * 1.8));
        if (groupRef.current) {
          groupRef.current.style.transform = `scale(${s.toFixed(4)})`;
          groupRef.current.style.opacity = o.toFixed(3);
        }
      } else if (variant === "trace") {
        // draw-on stroke that loops, fill fades in behind it
        const cycle = (elapsed * 0.5) % 1;
        const ease =
          cycle < 0.5 ? 2 * cycle * cycle : 1 - Math.pow(-2 * cycle + 2, 2) / 2;
        const LEN = 320;
        if (groupRef.current) {
          const paths = groupRef.current.querySelectorAll(".trace");
          paths.forEach((p) => {
            p.style.strokeDashoffset = `${LEN * (1 - ease)}`;
          });
          const fill = groupRef.current.querySelector(".trace-fill");
          if (fill) fill.style.opacity = Math.max(0, (ease - 0.55) / 0.45).toFixed(3);
        }
      }
      rafId.current = requestAnimationFrame(loop);
    };
    rafId.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafId.current);
  }, [variant, speed, reducedMotion]);

  const stroke = bgDark ? "#FFFFFF" : "#07054B";
  const idle = bgDark ? "rgba(255,255,255,0.10)" : "rgba(7,5,75,0.07)";
  const legFill = bgDark ? "#FFFFFF" : "#07054B";
  const gradientId = "rloader-grad-" + accent.replace("#", "");

  // Accessibility: when the user prefers reduced motion, show a static filled
  // mark instead of running the animation loop.
  if (reducedMotion) {
    return (
      <svg
        width={size}
        height={size}
        viewBox="-6 -6 112 112"
        style={{ overflow: "visible", display: "block" }}
        role="img"
        aria-label="Loading"
      >
        <path d={RAINDROP} fill={accent} />
        <path d={LEG} fill={legFill} />
      </svg>
    );
  }

  return (
    <svg
      width={size}
      height={size}
      viewBox="-6 -6 112 112"
      style={{ overflow: "visible", display: "block" }}
      role="img"
      aria-label="Loading"
    >
      <defs>
        <clipPath id="rloader-clip">
          <path d={RAINDROP} />
          <path d={LEG} />
        </clipPath>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={accent} stopOpacity="0.92" />
          <stop offset="100%" stopColor={accent} />
        </linearGradient>
      </defs>

      {variant === "liquid" && (
        <g>
          <g clipPath="url(#rloader-clip)">
            <rect x="-12" y="-12" width="124" height="124" fill={idle} />
            <path ref={waterRef} fill={`url(#${gradientId})`} d={liquidPath(52, 0, 3.2)} />
            <path ref={shineRef} fill="#FFFFFF" fillOpacity="0.16" d={liquidPath(50.6, 0.6, 2.2)} />
          </g>
          <path d={RAINDROP} fill="none" stroke={stroke} strokeOpacity="0.9" strokeWidth="1.4" strokeLinejoin="round" />
          <path d={LEG} fill="none" stroke={stroke} strokeOpacity="0.9" strokeWidth="1.4" strokeLinejoin="round" />
        </g>
      )}

      {variant === "breathe" && (
        <g
          ref={groupRef}
          style={{
            transformOrigin: "50px 50px",
            filter: `drop-shadow(0 6px 20px ${accent}66)`,
            transition: "none",
          }}
        >
          <path d={RAINDROP} fill={accent} />
          <path d={LEG} fill={legFill} />
        </g>
      )}

      {variant === "trace" && (
        <g ref={groupRef}>
          <g className="trace-fill" style={{ opacity: 0 }}>
            <path d={RAINDROP} fill={accent} />
            <path d={LEG} fill={legFill} />
          </g>
          <path
            className="trace"
            d={RAINDROP}
            fill="none"
            stroke={accent}
            strokeWidth="2"
            strokeLinejoin="round"
            strokeLinecap="round"
            style={{ strokeDasharray: 320, strokeDashoffset: 320 }}
          />
          <path
            className="trace"
            d={LEG}
            fill="none"
            stroke={legFill}
            strokeWidth="2"
            strokeLinejoin="round"
            strokeLinecap="round"
            style={{ strokeDasharray: 320, strokeDashoffset: 320 }}
          />
        </g>
      )}
    </svg>
  );
}
